import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, X, Check } from "lucide-react";
import { IMG } from "../data/content";
import Seo from "../components/Seo";
import SpiritCallout from "../components/SpiritCallout";

const CASCADE = [
  { step: "01", label: "Seed Quantity", note: "Where most conversations begin." },
  { step: "02", label: "Species Diversity", note: "And where they typically stop." },
  { step: "03", label: "Genetic Diversity", note: "The first level the sector often skips." },
  { step: "04", label: "Adaptive Capacity", note: "Populations that can respond to a changing climate." },
  { step: "05", label: "Resilient Landscapes", note: "Restoration that holds, generation after generation." },
];

const PRIORITIES = [
  { title: "Provenance", body: "Seed sourced and matched to Country — its origins, climate window and cultural significance." },
  { title: "Genetic Diversity", body: "Collections that capture the breadth of a population, not the convenience of a single tree." },
  { title: "Adaptation", body: "Choices that anticipate the climate populations will live in — not only the one they grew in." },
  { title: "Future Resilience", body: "Populations capable of reproducing, recruiting and persisting over decades." },
  { title: "Long-term Ecosystem Function", body: "Restoration that re-establishes the relationships — soil, pollinators, fire — not just the plants." },
];

export default function Genetics() {
  return (
    <div data-testid="page-genetics">
      <Seo
        title="Genetics, Provenance & Future Landscapes"
        description="Restoration is not simply about getting plants back into the ground. It is about rebuilding resilient plant populations capable of adapting to future conditions."
        path="/genetics"
      />
      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="stamp">Core to SeedKeepers</span>
              <span className="specimen-label">Genetics, Provenance & Future Landscapes</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Restoration is not simply about getting plants back into the ground.
              <br />
              <em className="text-eucalypt">It is about rebuilding resilient plant populations.</em>
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Every seed carries genetic information shaped by thousands of
              years of adaptation to local conditions. The decisions made today
              about seed sourcing and plant genetics will determine the health
              and resilience of Australia's future ecosystems.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              That is a genetics conversation. And it is one of the most
              important conversations the restoration sector is not yet having
              at scale.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img src={IMG.seedPods} alt="Native seed pods, close-up" className="w-full h-[440px] object-cover bg-cream" />
            </div>
          </div>
        </div>
      </section>

      {/* The Mistake — high-impact contrast block */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="specimen-label text-chalk/60 mb-4">The Mistake Most People Make</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl mb-16">
            Many people think restoration success is one question. It is a different question entirely.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-chalk/15" data-testid="genetics-mistake">
            <div className="bg-bark p-8 md:p-12">
              <div className="flex items-center gap-3 mb-5">
                <X size={20} className="text-terracotta" />
                <span className="specimen-label text-chalk/60">The common question</span>
              </div>
              <p className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-chalk/85">
                "Did the plants grow?"
              </p>
              <p className="mt-6 text-sm text-chalk/60 leading-relaxed">
                Easy to measure. Easy to report. Easy to fund. But it tells you
                very little about whether restoration has actually happened.
              </p>
            </div>
            <div className="bg-bark p-8 md:p-12 border-l border-chalk/15">
              <div className="flex items-center gap-3 mb-5">
                <Check size={20} className="text-ochre" />
                <span className="specimen-label text-ochre">The more useful question</span>
              </div>
              <p className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.1]">
                "Will those populations persist, reproduce and adapt over the next 50–100 years?"
              </p>
              <p className="mt-6 text-sm text-chalk/70 leading-relaxed">
                Harder. Slower. More expensive to monitor. But it is the only
                question whose answer is restoration.
              </p>
            </div>
          </div>

          <p className="mt-14 font-serif italic text-2xl md:text-3xl text-ochre max-w-3xl">
            That's where genetics enters the picture.
          </p>
        </div>
      </section>

      {/* SeedKeepers Priorities — quantity AND… */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">What we are interested in</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl mb-4">
            SeedKeepers is interested in seed quantity.
          </h2>
          <h2 className="font-serif italic text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-eucalypt leading-[1.02] max-w-4xl mb-14">
            And in the five things almost no one is measuring.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule" data-testid="genetics-priorities">
            {PRIORITIES.map((p, i) => (
              <div key={p.title} className="bg-card p-8 md:p-10 flex flex-col gap-4 min-h-[260px]" data-testid={`priority-${i}`}>
                <span className="specimen-label">Priority {String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight">{p.title}</h3>
                <p className="text-sm md:text-base text-ink/80 leading-relaxed">{p.body}</p>
              </div>
            ))}
            <div className="bg-card p-8 md:p-10 flex flex-col gap-4 min-h-[260px] border-l-2 border-ochre2">
              <span className="specimen-label text-ochre2">Across all five</span>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-ink">
                Restoration that holds — long after the funding cycle ends.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cascade graphic */}
      <section className="border-b border-rule bg-parchment/40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">The Cascade</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] mb-3">
            Most restoration conversations stop at the first two levels.
          </h2>
          <p className="font-serif italic text-2xl md:text-3xl text-ochre2 mb-16">
            The future of restoration sits in the last three.
          </p>

          <div className="space-y-2 max-w-2xl mx-auto" data-testid="genetics-cascade">
            {CASCADE.map((c, i) => {
              const isLate = i >= 2;
              return (
                <div key={c.step}>
                  <div
                    className={`border p-6 md:p-7 flex items-center justify-between gap-6 ${
                      isLate ? "border-ochre2 bg-card" : "border-rule bg-card"
                    }`}
                    data-testid={`cascade-${c.step}`}
                  >
                    <div className="flex items-center gap-5">
                      <span className={`font-mono text-xs tracking-[0.25em] ${isLate ? "text-ochre2" : "text-muted"}`}>
                        Level {c.step}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className={`font-serif text-2xl md:text-3xl leading-tight ${isLate ? "text-ink" : "text-muted"}`}>
                        {c.label}
                      </p>
                      <p className="text-xs text-muted mt-1">{c.note}</p>
                    </div>
                    {isLate && (
                      <span className="text-[10px] tracking-[0.25em] uppercase text-ochre2 border border-ochre2 px-2 py-1 hidden md:inline-block">
                        Future
                      </span>
                    )}
                  </div>
                  {i < CASCADE.length - 1 && (
                    <div className="flex justify-center py-1">
                      <ArrowDown size={18} className={i >= 1 ? "text-ochre2" : "text-rule"} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Lane */}
      <section className="border-b border-rule">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <p className="section-num mb-3">Our Lane</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight mb-8">
            SeedKeepers is not a genetics research organisation.
          </h2>
          <p className="text-base md:text-lg text-ink/85 leading-relaxed max-w-3xl">
            We help connect Traditional Owner knowledge, seed systems,
            provenance considerations and emerging genetic science to support
            better restoration outcomes.
          </p>
          <p className="mt-6 text-base md:text-lg text-ink/85 leading-relaxed max-w-3xl">
            That positions SeedKeepers as a bridge between communities,
            restoration practitioners, nurseries, researchers, forestry and
            mining rehabilitation.
          </p>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-px bg-rule border border-rule">
            {["Community", "Restoration practitioners", "Nurseries", "Researchers", "Forestry", "Mining rehabilitation"].map((g) => (
              <div key={g} className="bg-card p-5 md:p-6">
                <span className="specimen-label">Bridge to</span>
                <p className="font-serif text-xl md:text-2xl mt-2 leading-tight">{g}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPIRIT Platform — partner CTA */}
      <SpiritCallout
        variant="condensed"
        sectionLabel="The SPIRIT Platform"
        id="spirit"
        source="genetics"
      />

      {/* Closing */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-8">The Question</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            The question is no longer whether we have enough seed.
            <br />
            <span className="italic text-eucalypt">
              The question is whether we are collecting, storing and deploying
              the right genetics to support the landscapes of the future.
            </span>
          </h2>
          <div className="mt-12">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
              data-testid="genetics-contact-cta"
            >
              Talk to us about provenance <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
